/* eslint-disable @typescript-eslint/no-unused-vars */
import { TableProps } from 'antd';
import { PopoverHeaderCell } from 'pages/SalesSettlement/schema';
import React from 'react';
import moment from 'moment';
import { type TPartnerContract, TAssigneeManager, TPartner } from 'hooks';

import { DividerWrapper, ActionButton, TableTitleContainer } from './styles';

interface CollapseHeaderProps {
  label: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}
export const Divider = () => <DividerWrapper>|</DividerWrapper>;
export const TableTitle = ({ label, description, action }: CollapseHeaderProps) => {
  return (
    <TableTitleContainer gap="8px">
      <div className="collapse-header">{label}</div>
      {description ? <Divider /> : null}
      {description ? <div className="collapse-desc">{description}</div> : null}
      {action ? <div style={{ marginLeft: 'auto' }}>{action}</div> : null}
    </TableTitleContainer>
  );
};

const certificateStatusMap = {
  NONE: { label: '등록 필요', color: '#FF3B3E' },
  CONFIRM: { label: '등록 완료', color: '#1DB53A' },
  EXPIRED: { label: '등록 필요', color: '#FF3B3E' }
};
const popoverContentMap = {
  A: `이용회사와 제휴점간 직접정산을 진행하는 경우, 회사 직접정산 금액은 총 정산금액에서 제외됩니다.`, // 회사 직접정산 금액
  B: `증빙마감일 이전에 세금계산서(증빙)발급을 완료 해야 정해진 지급일자에 정산금 지급이 가능합니다.`, // 증빙 마감일
  payDay: `증빙마감일을 초과하여 세금계산서(증빙)발급을 완료 한다면, 입금 예정일이 달라 질 수 있습니다.`, // 입금 예정일
  isEvidenceMerge: `① 상계: 매출금액에서 수수료를 차감한 정산금액을 기준으로 매출 세금계산서 한장으로 증빙하는 방식입니다.\n② 별도: 매출금액과 수수료를 별도의 매출/매입 세금계산서 두장으로 증빙하는 방식입니다.`, // 적격증빙 상계 여부
  isPayCommissionMerge: `① 상계: 매출금액에서 수수료를 차감한 금액만 정산금으로 하여 지급합니다.\n② 별도: 매출금액은 지급하고, 수수료는 별도의 식권대장계좌로 입금받는 방식입니다.`, // 이체 상계 여부
  feeCalcTypeName: `① 거래건 별 : 거래건 별로 수수료율을 적용하여 계산하는 방식입니다.\n② 회사 별 : 이용 회사 별로 거래금액의 합에 수수료율을 적용하여 계산하는 방식입니다.\n③ 제휴점 별 : 제휴점의 거래금액의 합에 수수료율을 적용하여 계산하는 방식입니다.` // 수수료 계산방식
};
const taxableEvidenceMap = {
  ELECTRONIC_CERTIFICATE: (
    <div>
      사장님이 직접 <span style={{ fontWeight: 700 }}>[식권대장 사장님] 웹 사이트</span>에 공동인증서를 등록하신 후,{' '}
      <span style={{ fontWeight: 700 }}>앱 또는 웹</span>에서 세금계산서 발급을 진행하셔야 합니다.
    </div>
  ),
  ISSUED_BY_PROVIDER: (
    <div>
      사장님이 직접 <span style={{ fontWeight: 700 }}>국세청 사이트에 접속</span>하여, 세금계산서를 별도 발급하셔야 합니다.
    </div>
  ),
  PAPER_TAX_INVOICE: (
    <div>
      제공되는 매출 증빙 자료를 통하여, <span style={{ fontWeight: 700 }}>정산 이후</span> 사장님이 직접{' '}
      <span style={{ fontWeight: 700 }}>세무 신고</span>를 진행하셔야 합니다.
    </div>
  )
};

// 계약서 기본 정보
export const partnerContractColumns: TableProps<TPartnerContract>['columns'] = [
  { title: '정산 주기 시작일', dataIndex: 'settlementCycleDay', align: 'center' },
  {
    title: <PopoverHeaderCell title="입금예정일" popoverContent={popoverContentMap.payDay} />,
    dataIndex: 'payDay',
    align: 'center'
  },
  {
    title: <PopoverHeaderCell title="적격증빙 상계 여부" popoverContent={popoverContentMap.isEvidenceMerge} />,
    dataIndex: 'isEvidenceMerge',
    align: 'center',
    render: (value) => (value ? '상계' : '별도')
  },
  {
    title: <PopoverHeaderCell title="이체 상계 여부" popoverContent={popoverContentMap.isPayCommissionMerge} />,
    dataIndex: 'isPayCommissionMerge',
    align: 'center',
    render: (value) => (value ? '상계' : '별도')
  },
  {
    title: <PopoverHeaderCell title="수수료 계산 방식" popoverContent={popoverContentMap.feeCalcTypeName} />,
    dataIndex: 'feeCalcTypeName',
    align: 'center'
  },
  {
    title: '포인트 결제 수수료',
    dataIndex: 'pointCommissionRate',
    align: 'center'
    // render: (value) => `${value}%`
  },
  {
    title: '대장페이 결제 수수료',
    dataIndex: 'captainPayCommissionRate',
    align: 'center'
    // render: (value) => `${value}%`
  },
  {
    title: '입금 받을 계좌',
    dataIndex: 'name',
    align: 'center',
    render: (_, data) => {
      return `${data?.accountBankName || '-'} / ${data?.accountBankAccountNumber || '-'} / ${data?.accountBankAccountHolder || '-'}`;
    }
  }
];
// 사업자 정보
export const bizAccountColumns: TableProps<TPartnerContract>['columns'] = [
  { title: '상호명', dataIndex: 'bizName', align: 'center' },
  { title: '사업자번호', dataIndex: 'bizNumber', align: 'center' },
  { title: '종사업장번호', dataIndex: 'bizSubNumber', align: 'center' },
  { title: '대표자명', dataIndex: 'bizPresidentName', align: 'center' },
  { title: '주소', dataIndex: 'bizAddress', align: 'center' },
  { title: '업태', dataIndex: 'bizCategory', align: 'center' },
  { title: '종목', dataIndex: 'bizItem', align: 'center' }
];
// 인증서 정보
export const certificateColumns: TableProps<TPartnerContract>['columns'] = [
  { title: '증빙 발행 유형', dataIndex: 'taxableEvidenceTypeName', align: 'center' },
  {
    title: '설명',
    dataIndex: 'taxableEvidenceType',
    align: 'center',
    render: (value) => (value ? taxableEvidenceMap[value] : '-')
  },
  {
    title: '인증서 등록일',
    dataIndex: 'certificateIssuedDate',
    align: 'center',
    render: (value, values) => {
      if (values?.taxableEvidenceType !== 'ELECTRONIC_CERTIFICATE') return '해당 없음';
      return value || '-';
    }
  },
  {
    title: '인증서 만료일',
    dataIndex: 'certificateExpiredDate',
    align: 'center',
    render: (value, values) => {
      if (values?.taxableEvidenceType !== 'ELECTRONIC_CERTIFICATE') return '해당 없음';
      return value || '-';
    }
  },
  {
    title: '인증서 등록 상태',
    align: 'center',
    render: (_, values) => {
      if (values?.taxableEvidenceType !== 'ELECTRONIC_CERTIFICATE') return '해당 없음';

      const { certificateIssuedDate, certificateExpiredDate } = values || {};

      const getCertificateStatus = () => {
        if (!certificateIssuedDate && !certificateExpiredDate) return 'NONE';
        if (certificateIssuedDate && !certificateExpiredDate) return 'CONFIRM';
        if (certificateIssuedDate && certificateExpiredDate && moment().isAfter(certificateExpiredDate)) return 'EXPIRED';
        return 'CONFIRM';
      };

      const status = certificateStatusMap[getCertificateStatus()];
      return <div style={{ fontWeight: '700', color: status.color }}>{status.label}</div>;
    }
  }
];
// 정산 담당자 정보
export const assigneeManagerColumns = ({ onManagerModal, onDeleteAssigneeManager }): TableProps<TAssigneeManager>['columns'] => [
  { title: '담당자명', dataIndex: 'managerName', align: 'center' },
  { title: '이메일', dataIndex: 'managerEmail', align: 'center' },
  { title: '연락처', dataIndex: 'managerPhoneNumber', align: 'center' },
  {
    title: '세금계산서 이메일 수신여부',
    dataIndex: 'isReceivedTaxInvoice',
    align: 'center',
    render: (value) => (value ? '수신' : '미수신')
  },
  {
    title: '비고',
    dataIndex: 'isReceivedTaxInvoice',
    align: 'center',
    render: (value) => (value ? `세금계산서 이메일` : '미수신')
  },
  {
    title: '수정 / 삭제',
    dataIndex: 'name',
    align: 'center',
    width: '120px',
    render: (_, values) => (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', width: '120px' }}>
        <ActionButton $width="70px" onClick={(event) => onManagerModal(event, values)}>
          수정
        </ActionButton>
        <ActionButton danger $width="70px" onClick={(event) => onDeleteAssigneeManager(event, values)}>
          삭제
        </ActionButton>
      </div>
    )
  }
];
// 연결 제휴점 정보
export const partnerColumns: TableProps<TPartner>['columns'] = [
  { title: '제휴점명', dataIndex: 'storeName', align: 'center', width: '25%' },
  { title: '주소', dataIndex: 'address', align: 'center', width: '25%' },
  { title: '매장 전화번호', dataIndex: 'storePhone', align: 'center', width: '25%' },
  { title: '담당자 전화번호', dataIndex: 'phoneNumber', align: 'center', width: '25%' }
];
